import type { StudentClaim } from "@/api/studentClaims";
import { StudentScreenshotStatus } from "@/stores/v2/studentStateStore";
import type { User } from "@/js/utils/userUtils";
import { studentClaimsToMapByStudentId } from "@/query/studentClaimsQuery";

/**
 * Sort order for the regular classroom grid.
 *
 * **Primary buckets (in order):**
 * 1. Pinned, managed here only (no other-class claim with `teacherName`)
 * 2. Pinned, managed elsewhere (other-class claim includes `teacherName`)
 * 3. Unpinned, managed here only
 * 4. Unpinned, managed elsewhere
 *
 * **Within each bucket (connection / activity):**
 * 1. Students with screenshots (`user.screenshots.length > 0`)
 * 2. Connecting (`CONNECTING`, no screenshots yet)
 * 3. Connection timeout (`NO_ACTIVITY`)
 * 4. Other / error-style states (`DISCONNECTED`, `RETRIED`, `ISSUE_ONGOING`, `RESOLVED`, `ACTIVE` without screenshots, etc.)
 *
 * **Then:** paused (internet-locked) students before non-paused (legacy `sortUsersByActivity` behaviour), then name sort.
 *
 * **Complexity:** O(n) to build lookup maps + O(n log n) for `.sort` with O(1) comparator keys — avoids repeated `find` / filter inside comparators (which would be O(n² log n)).
 */
export type SortRegularClassroomStudentsInput = {
	studentClaims: StudentClaim[];
	currentClassStableId: string;
	selectedClassroomStudents: Array<{ username: string; pinned: boolean }> | null | undefined;
	/** Per `studentStableIdV1` for this class session (from `useStudentState` slice). */
	studentScreenshotStatuses: Record<string, StudentScreenshotStatus> | undefined;
	sortByLastName: boolean;
	lockedUsernames: ReadonlySet<string>;
};

/**
 * True when claims data shows this student is actively managed in another class
 * (claim for their `qoriaUserId` includes `teacherName`).
 */
function isManagedElsewhere(user: User, claimsByStudentId: Record<string, StudentClaim>, currentClassStableId: string): boolean {
	const qid = user.qoriaUserId;
	if (!qid) return false;
	return Boolean(claimsByStudentId[qid]?.classId !== currentClassStableId);
}

/** Lower rank = earlier in the list. */
export function getConnectionActivityRank(user: User, screenshotStatus: StudentScreenshotStatus | undefined): number {
	const hasScreenshots = !!(user.screenshots && Array.isArray(user.screenshots) && user.screenshots.length > 0);
	if (hasScreenshots) {
		return 0;
	}
	const status = screenshotStatus ?? StudentScreenshotStatus.CONNECTING;
	if (status === StudentScreenshotStatus.CONNECTING) {
		return 1;
	}
	if (status === StudentScreenshotStatus.NO_ACTIVITY) {
		return 2;
	}
	return 3;
}

/** Bucket index 0..3 matching product order. */
function getPrimaryBucket(pinned: boolean, managedElsewhere: boolean): number {
	if (pinned && !managedElsewhere) return 0;
	if (pinned && managedElsewhere) return 1;
	if (!pinned && !managedElsewhere) return 2;
	return 3;
}

function compareUsersByNameAndUsername(a: User, b: User, sortByLastName: boolean): number {
	const aUsername = a.username;
	const bUsername = b.username;

	if (a.fname && !b.fname) return -1;
	if (!a.fname && b.fname) return 1;
	if (a.fname && b.fname && !sortByLastName) {
		const fnameCompared = a.fname.toLowerCase().localeCompare(b.fname.toLowerCase());
		if (fnameCompared === 0 && a.lname && b.lname) {
			const lnameCompared = a.lname.toLowerCase().localeCompare(b.lname.toLowerCase());
			if (lnameCompared === 0) {
				return aUsername.localeCompare(bUsername);
			}
			return lnameCompared;
		}
		return fnameCompared;
	}
	if (a.lname && b.lname && sortByLastName) {
		const lnameCompared = a.lname.toLowerCase().localeCompare(b.lname.toLowerCase());
		if (lnameCompared === 0 && a.fname && b.fname) {
			const fnameCompared = a.fname.toLowerCase().localeCompare(b.fname.toLowerCase());
			if (fnameCompared === 0) {
				return aUsername.localeCompare(bUsername);
			}
			return fnameCompared;
		}
		return lnameCompared;
	}
	if (aUsername !== undefined && bUsername !== undefined) {
		return aUsername.localeCompare(bUsername);
	}
	if (aUsername === undefined && bUsername !== undefined) return -1;
	if (aUsername !== undefined && bUsername === undefined) return 1;
	return 0;
}

export function sortRegularClassroomStudents(users: User[], input: SortRegularClassroomStudentsInput): User[] {
	const claimsByStudentId = studentClaimsToMapByStudentId(input.studentClaims);
	const pinnedByUsername = new Map<string, boolean>();
	input.selectedClassroomStudents?.forEach((s) => {
		pinnedByUsername.set(s.username, s.pinned);
	});

	const statusByStudent = input.studentScreenshotStatuses ?? {};

	const keys = new Map<
		string,
		{
			bucket: number;
			connectionRank: number;
			paused: boolean;
		}
	>();

	for (const u of users) {
		const pinned = pinnedByUsername.get(u.username) ?? false;
		const managedElsewhere = isManagedElsewhere(u, claimsByStudentId, input.currentClassStableId);
		const bucket = getPrimaryBucket(pinned, managedElsewhere);
		const st = statusByStudent[u.stableIdV1];
		const connectionRank = getConnectionActivityRank(u, st);
		const paused = input.lockedUsernames.has(u.username);
		keys.set(u.username, { bucket, connectionRank, paused });
	}

	return [...users].sort((a, b) => {
		const userA = keys.get(a.username);
		const userB = keys.get(b.username);
		if (!userA || !userB) return 0;
		if (userA.bucket !== userB.bucket) return userA.bucket - userB.bucket;
		if (userA.connectionRank !== userB.connectionRank) return userA.connectionRank - userB.connectionRank;
		if (userA.paused && !userB.paused) return -1;
		if (!userA.paused && userB.paused) return 1;
		return compareUsersByNameAndUsername(a, b, input.sortByLastName);
	});
}
